import React, { useState } from "react";
import { BADGES_BY_ID, closestUnearned } from "../helpers/badges";

// A row of small badge icons (leaderboard rows, own profile card). `ids` are earned badge ids.
export function BadgeIcons({ ids, max = 4 }) {
  const list = (ids || []).map((id) => BADGES_BY_ID[id]).filter(Boolean);
  if (!list.length) return null;
  const shown = list.slice(-max); // the most advanced badges are defined last, so show those
  const hidden = list.length - shown.length;
  return (
    <span className="badge-icons" aria-label={`${list.length} badge${list.length === 1 ? "" : "s"}: ${list.map((b) => b.name).join(", ")}`}>
      {shown.map((b) => <span key={b.id} className="badge-icon" title={`${b.name}: ${b.description}`} aria-hidden="true">{b.icon}</span>)}
      {hidden > 0 && <span className="badge-more" aria-hidden="true">+{hidden}</span>}
    </span>
  );
}

// The full badge section of a profile. `badges` is the API's list: every badge with { value, goal, earned }.
export function BadgeGrid({ badges, isMe }) {
  const [showLocked, setShowLocked] = useState(false);
  const earned = badges.filter((b) => b.earned);
  const locked = badges.filter((b) => !b.earned);
  const next = closestUnearned(badges);

  return (
    <section className="badge-section" aria-label="Badges">
      <div className="badge-head">
        <h4>Badges</h4>
        <span>{earned.length} of {badges.length}</span>
      </div>

      {earned.length === 0 ? (
        <p className="panel-muted">
          {isMe ? "You haven't earned a badge yet. Get a wreck or an edit approved to earn your first." : "No badges yet."}
        </p>
      ) : (
        <ul className="badge-grid">
          {earned.map((b) => (
            <li key={b.id} className="badge-card earned" title={b.description}>
              <span className="badge-emoji" aria-hidden="true">{b.icon}</span>
              <strong>{b.name}</strong>
              <small>{b.description}</small>
            </li>
          ))}
        </ul>
      )}

      {next && (
        <p className="badge-next">
          <span aria-hidden="true">{next.icon}</span> Next up: <strong>{next.name}</strong> ({Math.min(next.value, next.goal)}/{next.goal})
        </p>
      )}

      {locked.length > 0 && (
        <>
          <button type="button" className="panel-link" onClick={() => setShowLocked((v) => !v)} aria-expanded={showLocked}>
            {showLocked ? "Hide" : "Show"} the {locked.length} you haven't earned yet
          </button>
          {showLocked && (
            <ul className="badge-grid">
              {locked.map((b) => {
                const pct = Math.min(100, Math.round((b.value / b.goal) * 100));
                return (
                  <li key={b.id} className="badge-card locked" title={b.description}>
                    <span className="badge-emoji" aria-hidden="true">{b.icon}</span>
                    <strong>{b.name}</strong>
                    <small>{b.description}</small>
                    <span className="badge-bar" role="progressbar" aria-label={`${b.name} progress`} aria-valuemin={0} aria-valuemax={b.goal} aria-valuenow={Math.min(b.value, b.goal)}>
                      <span style={{ width: `${pct}%` }} />
                    </span>
                    <small className="badge-count">{Math.min(b.value, b.goal)} / {b.goal}</small>
                  </li>
                );
              })}
            </ul>
          )}
        </>
      )}
    </section>
  );
}
